// Client side of the compromised-device flow.
//
// When api() sees `X-Compromise-Signal: true` on any response (it rides a 401
// from the server's compromise gate), this:
//   1. drops the in-memory access token,
//   2. wipes the offline lead outbox — a reported device must not keep queued
//      prospect PII in plaintext IndexedDB (#204); the Settings UI and the API
//      401 both promise the offline store is wiped,
//   3. hard-navigates to /login — a full reload guarantees a clean slate.
//
// The wipe is best-effort and time-bounded: the session is already dead (token
// dropped, server revoked the chain), so a slow or blocked IndexedDB must never
// trap the user on the compromised view — the redirect fires once the wipe
// settles or a short timeout elapses, whichever comes first. Guarded so
// concurrent signalled responses trigger exactly one run.

import { setAccessToken } from './auth';

const WIPE_TIMEOUT_MS = 2000;

let wiping = false;
let settled: Promise<void> | null = null;

export function handleCompromiseSignal(): void {
  if (wiping) return;
  wiping = true;
  setAccessToken(null);
  settled = wipeOfflineData().finally(redirectToLogin);
}

// wipeOfflineData clears the field-capture outbox. The outbox module is loaded
// with a dynamic import so its IndexedDB code stays off the marketing bundle's
// 30 kB gzip budget (matching app.ts's lazy outbox load), and the clear races a
// timeout so a wedged IDB can't block the redirect.
async function wipeOfflineData(): Promise<void> {
  try {
    const { clear } = await import('./outbox');
    await Promise.race([clear(), timeout(WIPE_TIMEOUT_MS)]);
  } catch {
    // Best-effort — the hard reload below still tears the page down. The token
    // is already dropped and the server has revoked the session chain.
  }
}

function timeout(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined') window.setTimeout(resolve, ms);
    else resolve();
  });
}

// resetCompromiseGuard is for tests only — the guard is a module singleton.
export function resetCompromiseGuard(): void {
  wiping = false;
  settled = null;
}

// whenSettled resolves after the wipe has run and the redirect has fired. Tests
// only: the handler is fire-and-forget (void), so a test needs this to await
// the async wipe it kicks off before asserting on the redirect.
export function whenSettled(): Promise<void> {
  return settled ?? Promise.resolve();
}

function redirectToLogin(): void {
  // Hard navigation (matching auth.ts startOAuth), not an SPA route change: a
  // full document reload guarantees no in-memory state or timers survive the
  // compromise.
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign('/login');
  }
}
